import React, { useMemo, useState, useContext } from "react";

import CloseIcon from "@mui/icons-material/Close";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import {
	Box,
	Dialog,
	DialogContent,
	DialogTitle,
	IconButton,
	Tooltip,
	Typography,
	alpha,
	useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import * as echarts from "echarts";
import ReactECharts from "echarts-for-react";
import { Translate, GetLanguageValueAsString } from "@/app/application/Translate";
import { Language } from "@/model/Language/Language";

import { TranslationContext } from "@/app/providers";



/**
 * LineChartWithLimits component renders a line chart with measurement data and its limits.
 * It uses Chart.js for rendering the chart and Material-UI for UI components.
 * The chart displays measurements, minimum limits, and maximum limits over time.
 *
 * Props:
 * @param {Array} data - Array of data objects containing measurement information.
 *
 * Data Object Structure:
 * @typedef {Object} DataObject
 * @property {string} TestDate - The date of the test.
 * @property {number} Measurement - The measurement value.
 * @property {number} MinLimit - The minimum limit value.
 * @property {number} MaxLimit - The maximum limit value.
 * @property {string} ParameterName - The name of the measurement parameter.
 *
 * State:
 * @property {boolean} open - State to manage the full-screen dialog visibility.
 *
 * Functions:
 * @function handleOpen - Opens the full-screen dialog.
 * @function handleClose - Closes the full-screen dialog.
 *
 * Chart Data:
 * @constant {Object} chartData - Data configuration for Chart.js.
 * @constant {Array} chartData.labels - Array of formatted dates for the x-axis.
 * @constant {Array} chartData.datasets - Array of datasets for measurements, min limits, and max limits.
 *
 *
 * Usage:
 * ```tsx
 * <LineChartWithLimits data={data} />
 * ```
 */
interface DataObject {
	TestDate: string;
	Measurement: number;
	MinLimit: number;
	MaxLimit: number;
	ParameterName: string;
}

interface LineChartWithLimitsProps {
	data: DataObject[];
}

const LineChartWithLimits: React.FC<LineChartWithLimitsProps> = ({ data }) => {
	const translation: Language | null = useContext(TranslationContext);
	const [open, setOpen] = useState(false);
	const handleOpen = () => setOpen(true);
	const handleClose = () => setOpen(false);

	const theme = useTheme();

	const points = useMemo(
		() => (Array.isArray(data) ? [...data].reverse() : []),
		[data],
	);
	const labels = useMemo(
		() => points.map((entry) => dayjs(entry.TestDate).format("DD/MM/YY")),
		[points],
	);
	const measurements = useMemo(
		() => points.map((entry) => Number(entry.Measurement ?? 0)),
		[points],
	);
	const minLimits = useMemo(
		() => points.map((entry) => Number(entry.MinLimit ?? 0)),
		[points],
	);
	const maxLimits = useMemo(
		() => points.map((entry) => Number(entry.MaxLimit ?? 0)),
		[points],
	);
	const ySeriesValues = useMemo(
		() =>
			[...measurements, ...minLimits, ...maxLimits].filter((v) =>
				Number.isFinite(v),
			),
		[measurements, minLimits, maxLimits],
	);
	const yMin = ySeriesValues.length ? Math.min(...ySeriesValues) : 0;
	const yMax = ySeriesValues.length ? Math.max(...ySeriesValues) : 1;

	const tooltipMutedTextColor =
		theme.palette.mode === "dark"
			? "rgba(226,232,240,0.72)"
			: "rgba(71,85,105,0.9)";
	const tooltipShellSx = useMemo(
		() => ({
			renderMode: "html" as const,
			appendToBody: true,
			confine: false,
			enterable: true,
			transitionDuration: 0.08,
			backgroundColor:
				theme.palette.mode === "dark"
					? "rgba(15, 23, 42, 0.96)"
					: "rgba(255, 255, 255, 0.98)",
			borderColor: alpha(
				theme.palette.primary.main,
				theme.palette.mode === "dark" ? 0.45 : 0.22,
			),
			borderWidth: 1,
			borderRadius: 10,
			padding: [10, 12],
			textStyle: {
				color: theme.palette.text.primary,
				fontSize: 12,
			},
			extraCssText:
				"box-shadow: 0 10px 28px rgba(2,6,23,0.26); max-width: 360px; white-space: normal; z-index: 9999; backdrop-filter: blur(2px);",
		}),
		[theme],
	);
	const baseChartOption = useMemo<echarts.EChartsOption>(() => {
		const parameterName = points[0]?.ParameterName || "Measurement";

		return {
			backgroundColor: "transparent",
			color: [
				theme.palette.primary.main,
				theme.palette.success.main,
				theme.palette.error.main,
			],
			grid: { left: 52, right: 16, top: 20, bottom: 38 },
			legend: {
				top: 0,
				right: 4,
				textStyle: {
					color: theme.palette.text.secondary,
					fontSize: 11,
				},
			},
			tooltip: {
				...tooltipShellSx,
				trigger: "axis",
				axisPointer: { type: "line" },
				formatter: (params: unknown) => {
					const list = (Array.isArray(params) ? params : [params]) as Array<{
						dataIndex?: number;
						seriesName?: string;
						data?: number;
						marker?: string;
					}>;
					const first = list[0];
					const idx = Number(first?.dataIndex ?? 0);
					const measured = measurements[idx] ?? 0;
					const min = minLimits[idx] ?? 0;
					const max = maxLimits[idx] ?? 0;
					const status =
						measured < min || measured > max
							? "Outside limits"
							: "Within limits";

					return `
						<div style="max-width:330px">
							<div style="font-weight:700; margin-bottom:6px">${labels[idx] ?? ""}</div>
							${list
								.map(
									(item) => `
										<div style="display:flex; justify-content:space-between; gap:12px;">
											<span>${item.marker ?? ""}${item.seriesName ?? ""}</span>
											<strong>${Number(item.data ?? 0)}</strong>
										</div>
									`,
								)
								.join("")}
							<div style="margin-top:6px; color:${tooltipMutedTextColor}; font-size:12px;">
								${parameterName}: ${status}
							</div>
						</div>
					`;
				},
			},
			xAxis: {
				type: "category",
				data: labels,
				boundaryGap: false,
				axisLine: { show: false },
				axisTick: { show: false },
				axisLabel: {
					color: theme.palette.text.secondary,
					fontSize: 11,
					rotate: labels.length > 10 ? -25 : 0,
				},
			},
			yAxis: {
				type: "value",
				name: parameterName,
				nameTextStyle: {
					color: theme.palette.text.primary,
					fontWeight: 600,
					padding: [0, 0, 4, 0],
				},
				min: Math.floor(yMin - 1),
				max: Math.ceil(yMax + 1),
				splitLine: {
					show: true,
					lineStyle: { color: alpha(theme.palette.divider, 0.22) },
				},
				axisLine: { show: false },
				axisTick: { show: false },
				axisLabel: {
					color: theme.palette.text.secondary,
					fontSize: 11,
				},
			},
			series: [
				{
					name: "Measurement",
					type: "line",
					data: measurements,
					smooth: true,
					symbol: "circle",
					symbolSize: 6,
					lineStyle: { width: 2.5 },
					areaStyle: { opacity: theme.palette.mode === "dark" ? 0.24 : 0.12 },
				},
				{
					name: "Min Limit",
					type: "line",
					data: minLimits,
					smooth: true,
					symbol: "none",
					lineStyle: { width: 2, type: "dashed" },
				},
				{
					name: "Max Limit",
					type: "line",
					data: maxLimits,
					smooth: true,
					symbol: "none",
					lineStyle: { width: 2, type: "dashed" },
				},
			],
		};
	}, [
		labels,
		measurements,
		minLimits,
		maxLimits,
		points,
		theme,
		tooltipMutedTextColor,
		tooltipShellSx,
		yMax,
		yMin,
	]);

	return (
		<Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
			{/* Chart Component */}
			<Box
				sx={{
					position: "relative",
					width: "100%",
					height: 260,
					[theme.breakpoints.up("lg")]: {
						height: 300,
					},
				}}
			>
				<ReactECharts
					notMerge
					opts={{ renderer: "canvas" }}
					option={baseChartOption}
					style={{ height: "100%", width: "100%" }}
				/>
				{/* Full-Screen Button */}
				<Tooltip title={GetLanguageValueAsString(translation, "Auto.Features.Lab.Ui.Components.LineChartWithLimits.L291C20.OpenChartInFullScreen", "Open Chart in Full Screen")}>
					<IconButton
						aria-label={GetLanguageValueAsString(translation, "Auto.Features.Lab.Ui.Components.LineChartWithLimits.L293C18.OpenChartInFullScreen", "open chart in full screen")}
						onClick={handleOpen}
						sx={{
							position: "absolute",
							top: 8,
							right: 8,
							backgroundColor: "background.paper",
							color: "text.primary",
							"&:hover": {
								backgroundColor: "action.hover",
							},
						}}
					>
						<FullscreenIcon />
					</IconButton>
				</Tooltip>
			</Box>

			{/* Full-Screen Dialog */}
			<Dialog fullScreen open={open} onClose={handleClose}>
				<DialogTitle sx={{ display: "flex", justifyContent: "space-between" }}>
					<Typography variant="h6"><Translate String="Auto.Features.Lab.Ui.Components.LineChartWithLimits.L312C31.ChartDetails">Chart Details</Translate></Typography>
					<IconButton onClick={handleClose} aria-label={GetLanguageValueAsString(translation, "Auto.Features.Lab.Ui.Components.LineChartWithLimits.L315C51.Close", "close")}>
						<CloseIcon />
					</IconButton>
				</DialogTitle>
				<DialogContent
					sx={{
						bgcolor: "background.default",
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
					}}
				>
					<Box sx={{ width: "100%", height: "70vh", maxWidth: 1200 }}>
						<ReactECharts
							notMerge
							opts={{ renderer: "canvas" }}
							option={baseChartOption}
							style={{ height: "100%", width: "100%" }}
						/>
					</Box>
				</DialogContent>
			</Dialog>
		</Box>
	);
};

export default LineChartWithLimits;
